import { useState } from 'react';
import toast from 'react-hot-toast';
import { LinkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import { quoteService } from '@/services/quotes';
import { useAuthStore } from '@/store/auth';

const SHARING_ROLES = ['broker_admin', 'broker_employee', 'posp', 'super_posp'];

/**
 * BOARD #340 -- the customer-facing link, next to the internal proposal page.
 *
 * Choosing a premium tier keeps going to the broker's internal proposal form.
 * This is the other half: the customer's own quote page, the one that carries
 * the KYC step and the pay button. It reuses `quoteService.shareQuote`, which
 * publishes the request's existing public token (idempotent, broker-scoped on
 * the server, customers refused) -- no second token scheme.
 *
 * HONESTY. Nothing is delivered from here. The wording is "ready" and
 * "copied", never "sent": the broker still has to hand the link over.
 * `shareQuote` answers with a PATH, so the origin is added here.
 */
export function ShareCustomerLink({ requestId }: { requestId: string }) {
  const role = useAuthStore((state) => state.user?.role ?? '');
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!SHARING_ROLES.includes(role)) return null;

  const copy = async (link: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(link);
      return true;
    } catch {
      // Insecure origin or a refused permission: the link stays on screen.
      return false;
    }
  };

  const share = async () => {
    setBusy(true);
    setCopied(false);
    try {
      const data = await quoteService.shareQuote(requestId);
      const link = `${window.location.origin}${data.public_url}`;
      setUrl(link);
      const ok = await copy(link);
      setCopied(ok);
      toast.success(ok ? 'Customer link copied' : 'Customer link ready');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid="share-customer-link">
      <Button
        variant="outline"
        leftIcon={<LinkIcon className="w-5 h-5" />}
        onClick={share}
        isLoading={busy}
      >
        Share customer link
      </Button>
      {url && (
        <div className="text-sm">
          <input
            readOnly
            aria-label="Customer quote link"
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {copied ? 'Copied to your clipboard. ' : ''}
            This link is live for the customer. Nothing has been sent — pass it on yourself.
          </p>
          {!copied && (
            <Button size="sm" variant="ghost" onClick={async () => setCopied(await copy(url))}>
              Copy link
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
